import React, { useState, useEffect } from 'react';
import { Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { ThemeToggle } from './ThemeToggle';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { language, toggleLanguage, t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['home', 'about', 'products', 'services', 'faq', 'contact'];
      const current = sections.find(id => {
        const el = document.getElementById(id);
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return rect.top <= 100 && rect.bottom >= 100;
      });
      if (current) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setIsMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  const navLinks = [
    { id: 'home', label: t.nav.home },
    { id: 'about', label: t.nav.about },
    { id: 'products', label: t.nav.products },
    { id: 'services', label: t.nav.services },
    { id: 'faq', label: t.nav.faq },
    { id: 'contact', label: t.nav.contact }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 dark:bg-slate-950/95 backdrop-blur-md shadow-lg border-b border-transparent dark:border-slate-800' : 'bg-transparent'
        }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <button
            onClick={() => scrollToSection('home')}
            className="flex items-center space-x-1.5 group"
            data-testid="header-logo"
          >
            <div className="w-12 h-12 transition-transform group-hover:scale-110">
              <img
                src={isScrolled
                  ? "/images/lxb2qtht_C5.png"
                  : "/images/plbvvz5z_C3.png"}
                alt="Arrigo IOT Logo"
                className="w-full h-full object-contain drop-shadow-lg"
              />
            </div>
            <img
              src={isScrolled ? "/images/Q6.png" : "/images/Q3.png"}
              alt="Q Text"
              className="h-8 md:h-10 object-contain"
            />
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`text-sm font-medium transition-colors relative group ${activeSection === link.id
                  ? isScrolled ? 'text-purple-700 dark:text-purple-400' : 'text-purple-200'
                  : isScrolled
                    ? 'text-gray-700 hover:text-purple-700 dark:text-gray-300 dark:hover:text-purple-400'
                    : 'text-white hover:text-purple-200'
                  }`}
                data-testid={`nav-${link.id}`}
              >
                {link.label}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-purple-600 transition-all ${activeSection === link.id ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                />
              </button>
            ))}
          </nav>

          {/* Language Toggle, Theme Toggle & Mobile Menu */}
          <div className="flex items-center space-x-2 md:space-x-3">
            <ThemeToggle isScrolled={isScrolled} />
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className={`hidden md:flex items-center space-x-2 ${isScrolled ? 'text-gray-700 hover:text-purple-700 hover:bg-purple-50 dark:text-gray-200 dark:hover:text-purple-400 dark:hover:bg-slate-800' : 'text-white hover:text-purple-200 hover:bg-white/10'
                }`}
              data-testid="language-toggle-button"
            >
              <Globe className="w-4 h-4" />
              <span className="text-sm font-medium">{language.toUpperCase()}</span>
            </Button>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`md:hidden p-2 rounded-lg transition-colors ${isScrolled ? 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-slate-800' : 'text-white hover:bg-white/10'
                }`}
              data-testid="mobile-menu-toggle"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 bg-white dark:bg-slate-900 rounded-lg shadow-xl mt-2 mb-4 border border-transparent dark:border-slate-800">
            <nav className="flex flex-col space-y-1 px-4">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`px-4 py-3 rounded-lg text-sm font-medium text-left transition-colors ${activeSection === link.id
                    ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300'
                    : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800'
                    }`}
                  data-testid={`mobile-nav-${link.id}`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => {
                  toggleLanguage();
                  setIsMobileMenuOpen(false);
                }}
                className="px-4 py-3 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-slate-800 flex items-center space-x-2"
              >
                <Globe className="w-4 h-4" />
                <span>{language === 'en' ? 'Bahasa' : 'English'}</span>
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
